import React, { useState, useEffect, useCallback } from 'react';
import { Input, Button, Table, Tag, Spin, Toast, Tabs, TabPane } from '@douyinfe/semi-ui';
import { IconLock, IconRefresh, IconDelete } from '@douyinfe/semi-icons';

const PWD_KEY = 'whale-access-pwd';

const readPwd = () => {
  try { return localStorage.getItem(PWD_KEY) || ''; } catch { return ''; }
};

const writePwd = (v) => {
  try {
    if (v) localStorage.setItem(PWD_KEY, v);
    else localStorage.removeItem(PWD_KEY);
  } catch { /* ignore */ }
};

function fmtTime(ts) {
  if (!ts) return '-';
  const d = new Date(ts < 1e12 ? ts * 1000 : ts);
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const ipColumns = [
  { title: '排名', dataIndex: 'rank', width: 60, render: (t) => <Tag color="blue" size="small">{t}</Tag> },
  { title: 'IP', dataIndex: 'ip', width: 180 },
  { title: '访问次数', dataIndex: 'count', width: 100, sorter: (a, b) => a.count - b.count, defaultSortOrder: 'descend', render: (t) => <b>{t}</b> },
  { title: '最近访问', dataIndex: 'lastTime', width: 170, render: fmtTime },
];

const pathColumns = [
  { title: '路径', dataIndex: 'path' },
  { title: '访问次数', dataIndex: 'count', width: 120, sorter: (a, b) => a.count - b.count, defaultSortOrder: 'descend', render: (t) => <b>{t}</b> },
];

const AccessStats = () => {
  const [pwd, setPwd] = useState(readPwd());
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('ip');

  const fetchStats = useCallback(async (password) => {
    setLoading(true);
    try {
      const r = await fetch(`/access-stats?pwd=${encodeURIComponent(password)}`, { credentials: 'include' });
      const j = await r.json();
      if (r.status === 403) {
        Toast.error(j.msg || '密码错误');
        setUnlocked(false);
      } else if (j.total !== undefined || j.byIp) {
        setData(j);
        setUnlocked(true);
      } else {
        Toast.error(j.msg || '查询失败');
      }
    } catch (e) {
      Toast.error('请求失败：' + (e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleUnlock = () => {
    const p = (pwd || '').trim();
    if (!p) { Toast.warning('请输入访问密码'); return; }
    writePwd(p);
    fetchStats(p);
  };

  const handleClear = async () => {
    if (!window.confirm('确认清零所有访问统计？此操作不可恢复。')) return;
    setLoading(true);
    try {
      const r = await fetch(`/access-stats?pwd=${encodeURIComponent(pwd)}&clear=1`, { credentials: 'include' });
      const j = await r.json();
      if (j.status) {
        Toast.success('已清零');
        fetchStats((pwd || '').trim());
      } else {
        Toast.error(j.msg || '清零失败');
      }
    } catch (e) {
      Toast.error('清零失败：' + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const p = readPwd();
    if (p) fetchStats(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!unlocked) {
    return (
      <div className="w-access-lock">
        <div className="w-access-lock-card">
          <div className="w-access-lock-ico"><IconLock /></div>
          <div className="w-access-lock-title">访问统计</div>
          <div className="w-access-lock-sub">该面板仅限管理员查看，请输入访问密码</div>
          <div className="w-access-lock-row">
            <Input
              mode="password"
              placeholder="访问密码"
              value={pwd}
              onChange={(v) => setPwd(v)}
              onPressEnter={handleUnlock}
              prefix={<IconLock />}
              style={{ flex: 1 }}
            />
            <Button theme="solid" type="primary" onClick={handleUnlock} loading={loading}>解锁</Button>
          </div>
        </div>
      </div>
    );
  }

  const ipRows = (data?.byIp || []).map((r, i) => ({ ...r, rank: i + 1, key: r.ip }));
  const pathRows = (data?.byPath || []).map((r, i) => ({ ...r, key: i }));
  const recentRows = (data?.recent || []).map((r, i) => ({ ...r, key: i }));

  return (
    <div className="w-access-stats">
      <div className="w-access-head">
        <div className="w-access-head-left">
          <div className="w-access-title">📊 访问统计</div>
          <div className="w-access-sub">页面访问记录（不含 API 与静态资源）</div>
        </div>
        <div className="w-access-head-right">
          <Button icon={<IconRefresh />} onClick={() => fetchStats((pwd || '').trim())} loading={loading}>刷新</Button>
          <Button icon={<IconDelete />} type="danger" onClick={handleClear} loading={loading}>清零</Button>
        </div>
      </div>

      <div className="w-access-summary">
        <div className="w-access-summary-item highlight">
          <span>总访问量</span>
          <b>{data?.total ?? 0}</b>
        </div>
        <div className="w-access-summary-item">
          <span>独立 IP 数</span>
          <b>{(data?.byIp || []).length}</b>
        </div>
        <div className="w-access-summary-item">
          <span>路径数</span>
          <b>{(data?.byPath || []).length}</b>
        </div>
      </div>

      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} type="line">
          <TabPane tab="按 IP 统计" itemKey="ip">
            <Table
              columns={ipColumns}
              dataSource={ipRows}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOpts: [10, 20, 50] }}
              scroll={{ x: 'max-content' }}
              size="small"
              empty="暂无数据"
            />
          </TabPane>
          <TabPane tab="按路径统计" itemKey="path">
            <Table
              columns={pathColumns}
              dataSource={pathRows}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOpts: [10, 20, 50] }}
              scroll={{ x: 'max-content' }}
              size="small"
              empty="暂无数据"
            />
          </TabPane>
          <TabPane tab="最近访问" itemKey="recent">
            <Table
              columns={[
                { title: '时间', dataIndex: 'time', width: 170, render: fmtTime },
                { title: 'IP', dataIndex: 'ip', width: 180 },
                { title: '路径', dataIndex: 'path' },
                { title: 'UA', dataIndex: 'ua', render: (t) => <span title={t}>{(t || '').slice(0, 40)}</span> },
              ]}
              dataSource={recentRows}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOpts: [10, 20, 50] }}
              scroll={{ x: 'max-content' }}
              size="small"
              empty="暂无数据"
            />
          </TabPane>
        </Tabs>
      </Spin>
    </div>
  );
};

export default AccessStats;
